import { useCallback, useEffect, useRef, useState } from "react";
import type { ISecondaryContactV2 } from "../../../AddTravellerDetail/type";
import type { TravellerFieldArrayApi } from "../../TravellerDetailsV2.types";
import {
  clearPaxReloadExcelRestore,
  downloadTemplate,
  peekPaxReloadExcelRestore,
  setPaxReloadExcelRestore,
  setPostReloadPaxToastCookie,
} from "@tm/insurance/utils";
import { HEADER_MAPPING } from "../../../ExcelUpload/ExcelUpload.constants";
import { uploadTemplate } from "../../../ExcelUpload/utils";
import type { TravellerData } from "../../../ExcelUpload/ExcelUpload.types";
import type { BottomSheetConfig } from "./ExcelResultBottomSheet";
import {
  ERROR_CODE_MAP,
  PAX_RELOAD_MAX_FILE_EMBED_BYTES,
  excelDataToFormTravellers,
  parseExcelFile,
  replacePlaceholders,
  replaceInObj,
  type ParseResult,
  type PlaceholderValues,
} from "./excelParseUtils";

interface ExcelSectionData {
  title?: unknown;
  description?: unknown;
  bypassInfo?: string;
  info?: Record<string, any>;
  errors?: Record<string, string>;
  upload_error?: {
    action?: string;
    bottomsheetDetails?: {
      icon?: string;
      title?: string;
      subtitle?: string;
      cta?: Record<string, any>;
    };
  };
  download?: { btnText?: unknown };
  reUpload?: { btnText?: unknown };
  upload?: {
    buttonText?: unknown;
    maxFileSizeMB?: number;
    maxRows?: number;
    isExcelBypass?: boolean;
    requiredFields?: (keyof TravellerData)[];
  };
}

interface UseExcelUploadParams {
  excelSectionData: ExcelSectionData | undefined;
  travellerCount: number;
  pageData: { unitPrice?: number } | undefined;
  travellerFieldArray: TravellerFieldArrayApi;
  setExpandedTravellerIndex?: (value: React.SetStateAction<number>) => void;
  setForceShowAllTravellers?: (value: React.SetStateAction<boolean>) => void;
  setUploadedExcelFile?: (file: File | null) => void;
  onUploadFile?: (file: File) => void;
  onDownloadTemplate?: () => void;
}

export function useExcelUpload({
  excelSectionData,
  travellerCount,
  pageData,
  travellerFieldArray,
  setExpandedTravellerIndex,
  setForceShowAllTravellers,
  setUploadedExcelFile,
  onUploadFile,
  onDownloadTemplate,
}: UseExcelUploadParams) {
  const { replace } = travellerFieldArray;
  const isExcelBypass = excelSectionData?.upload?.isExcelBypass ?? false;

  const [isServerUploading, setIsServerUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [bsConfig, setBsConfig] = useState<BottomSheetConfig | null>(null);
  const [pendingApply, setPendingApply] = useState<{ data: TravellerData[]; file: File } | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const reUploadRef = useRef<HTMLInputElement>(null);

  const getQueryParam = useCallback((key: string) => {
    return new URLSearchParams(window.location.search).get(key) || "";
  }, []);

  const applyParsedData = useCallback(
    (data: TravellerData[], file: File) => {
      const limit = travellerCount || data.length;
      const sliced = data.slice(0, limit);
      const newTravellers = excelDataToFormTravellers(data, limit);
      replace(newTravellers);
      setExpandedTravellerIndex?.(-1);
      setUploadedFileName(file.name);
      setErrorMessage("");
      onUploadFile?.(file);
      setUploadedExcelFile?.(file);

      const reqFields = excelSectionData?.upload?.requiredFields;
      if (reqFields?.length) {
        const hasMissing = sliced.some((t) =>
          reqFields.some((f) => !t[f]?.trim()),
        );
        if (hasMissing) {
          const msg = excelSectionData?.errors?.["details_missing"] || "";
          setErrorMessage(replacePlaceholders(msg, { X: sliced.length, Y: 0, Z: travellerCount, W: 0, T: 0, unitPrice: 0 }));
        }
      }
    },
    [travellerCount, replace, onUploadFile, excelSectionData, setExpandedTravellerIndex, setUploadedExcelFile],
  );

  const resolveErrorMessage = useCallback(
    (errMsg: string): string => {
      const errors = excelSectionData?.errors;
      if (!errors) return errMsg;

      if (errMsg.startsWith("fields_missing:")) {
        const missing = errMsg.replace("fields_missing:", "").split(",");
        const nameMap: Record<string, string> = {
          name: "Name", dob: "DOB", gender: "Gender", passport: "Passport", nomineename: "Nominee Name",
        };
        const labels = missing.map((f) => nameMap[f.toLowerCase()] || f.charAt(0).toUpperCase() + f.slice(1)).join(", ");
        return (errors["fields_missing"] || "").replace("{fields}", labels);
      }

      const key = ERROR_CODE_MAP[errMsg] || "file_format";
      return errors[key] || errors["file_format"] || errMsg;
    },
    [excelSectionData],
  );

  const determineScenario = useCallback(
    (result: ParseResult, file: File) => {
      const info = excelSectionData?.info;
      if (!info) { applyParsedData(result.unique, file); return; }

      const uniqueCount = result.unique.length;
      const dupes = result.duplicatesRemoved;
      const totalExcelPax = result.totalParsed;
      const addedCount = Math.min(uniqueCount, travellerCount || uniqueCount);
      const remaining = Math.max(0, (travellerCount || 0) - addedCount);

      let scenarioKey: string;
      if (dupes > 0 && uniqueCount > travellerCount && travellerCount > 0) {
        scenarioKey = "traveller_added_duplicate_and_more";
      } else if (dupes > 0) {
        scenarioKey = "traveller_added_duplicate";
      } else if (uniqueCount > travellerCount && travellerCount > 0) {
        scenarioKey = "traveller_added_more";
      } else if (travellerCount > 0 && uniqueCount < travellerCount) {
        scenarioKey = "traveller_added_less";
      } else {
        scenarioKey = "traveller_added";
      }

      const scenarioConfig = info[scenarioKey] as any;

      if (!scenarioConfig || scenarioConfig.action === "toast" || typeof scenarioConfig === "string") {
        applyParsedData(result.unique, file);
        return;
      }

      if (scenarioConfig.action === "bottomsheet" && scenarioConfig.bottomsheetDetails) {
        const pv: PlaceholderValues = {
          X: addedCount,
          Y: remaining,
          Z: travellerCount,
          W: dupes,
          T: totalExcelPax,
          unitPrice: (pageData as any)?.unitPrice ?? 0,
        };
        const resolved = replaceInObj(scenarioConfig.bottomsheetDetails, pv) as BottomSheetConfig;

        setPendingApply({ data: result.unique, file });
        setBsConfig(resolved);
      } else {
        applyParsedData(result.unique, file);
      }
    },
    [excelSectionData, travellerCount, applyParsedData, pageData],
  );

  /* Restore Excel data persisted across pax-reload navigation */
  const paxReloadRestoreDoneRef = useRef(false);
  useEffect(() => {
    if (paxReloadRestoreDoneRef.current) return;
    const payload = peekPaxReloadExcelRestore();
    if (!payload?.travellerData?.length) return;
    paxReloadRestoreDoneRef.current = true;
    clearPaxReloadExcelRestore();
    replace(payload.travellerData as ISecondaryContactV2[]);
    setUploadedFileName(payload.fileName);
    setExpandedTravellerIndex?.(-1);
    setForceShowAllTravellers?.(payload.forceShowAll ?? true);
    setErrorMessage("");

    if (payload.fileDataUrl) {
      fetch(payload.fileDataUrl)
        .then((r) => r.blob())
        .then((blob) => {
          const f = new File(
            [blob],
            payload.fileName || "travellers.xlsx",
            { type: blob.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
          );
          setUploadedExcelFile?.(f);
          onUploadFile?.(f);
        })
        .catch(() => { /* file bar still shows name */ });
    }
  }, [replace, setUploadedExcelFile, onUploadFile, setForceShowAllTravellers, setExpandedTravellerIndex]);

  const uploadFileToServer = useCallback(
    async (file: File) => {
      setIsUploading(true);
      setErrorMessage("");

      try {
        const maxSize = (excelSectionData?.upload?.maxFileSizeMB ?? 10) * 1024 * 1024;
        if (file.size > maxSize) { setErrorMessage(resolveErrorMessage("file_size_exceeds")); return; }

        const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
        if (![".xlsx", ".xls"].includes(ext)) { setErrorMessage(resolveErrorMessage("unsupported_file_type")); return; }

        const XLSX = await import("xlsx");
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) { setErrorMessage(resolveErrorMessage("unable_to_read_file")); return; }

        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1 });
        if (jsonData.length < 2) { setErrorMessage(resolveErrorMessage("no_details_found")); return; }

        const headers = jsonData[0] as string[];
        const foundFields = new Set<keyof TravellerData>();
        headers.forEach((h) => {
          const key = HEADER_MAPPING[h?.toLowerCase().trim() || ""];
          if (key) foundFields.add(key);
        });

        const requiredFields = excelSectionData?.upload?.requiredFields;
        if (requiredFields) {
          const missing = requiredFields.filter((f) => !foundFields.has(f));
          if (missing.length) { setErrorMessage(resolveErrorMessage(`fields_missing:${missing.join(",")}`)); return; }
        }

        setIsServerUploading(true);
        const quoteId = getQueryParam("quoteId");
        const itineraryId = getQueryParam("itineraryId");

        try {
          const responseData = await uploadTemplate(file, quoteId, itineraryId, isExcelBypass);
          if (responseData) {
            setUploadedFileName(file.name);
            if (responseData.data?.checkoutUrl) { window.location.href = responseData.data.checkoutUrl; }
          }
        } catch (uploadErr) {
          const uploadMsg = uploadErr instanceof Error ? uploadErr.message : "Upload failed. Please try again.";
          setErrorMessage(uploadMsg);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "file_format";
        setErrorMessage(resolveErrorMessage(msg));
      } finally {
        setIsUploading(false);
        setIsServerUploading(false);
      }
    },
    [excelSectionData, isExcelBypass, resolveErrorMessage, getQueryParam],
  );

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;

      setErrorMessage("");
      setBsConfig(null);
      setPendingApply(null);

      if (isExcelBypass) { uploadFileToServer(file); return; }

      const maxSize = (excelSectionData?.upload?.maxFileSizeMB ?? 10) * 1024 * 1024;
      if (file.size > maxSize) { setErrorMessage(resolveErrorMessage("file_size_exceeds")); return; }

      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
      if (![".xlsx", ".xls"].includes(ext)) { setErrorMessage(resolveErrorMessage("unsupported_file_type")); return; }

      setIsUploading(true);
      try {
        const result = await parseExcelFile(
          file,
          excelSectionData?.upload?.maxRows ?? 100,
          excelSectionData?.upload?.requiredFields,
        );
        determineScenario(result, file);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "file_format";
        setErrorMessage(resolveErrorMessage(msg));
      } finally {
        setIsUploading(false);
      }
    },
    [excelSectionData, isExcelBypass, resolveErrorMessage, determineScenario, uploadFileToServer],
  );

  const handleBsAction = useCallback(
    (action: string, pax?: string, toast?: string) => {
      setBsConfig(null);

      if (action === "upload_file") { setPendingApply(null); fileRef.current?.click(); return; }

      if (action === "update_pax") {
        const newCount = parseInt(String(pax ?? "").trim(), 10);
        if (!pendingApply || !Number.isFinite(newCount) || newCount < 1) {
          if (pendingApply) { applyParsedData(pendingApply.data, pendingApply.file); setPendingApply(null); }
          return;
        }

        const { data, file } = pendingApply;
        const newTravellers = excelDataToFormTravellers(data, newCount);

        const navigateWithNewPax = (fileDataUrl: string | undefined) => {
          setPaxReloadExcelRestore({ travellerData: newTravellers, fileName: file.name, fileDataUrl, forceShowAll: true });
          const t = toast?.trim();
          if (t) setPostReloadPaxToastCookie(t);
          const url = new URL(window.location.href);
          url.searchParams.set("travellers", String(newCount));
          setPendingApply(null);
          window.location.assign(url.toString());
        };

        if (file.size <= PAX_RELOAD_MAX_FILE_EMBED_BYTES) {
          const reader = new FileReader();
          reader.onload = () => { navigateWithNewPax(typeof reader.result === "string" ? reader.result : undefined); };
          reader.onerror = () => navigateWithNewPax(undefined);
          reader.readAsDataURL(file);
        } else {
          navigateWithNewPax(undefined);
        }
        return;
      }

      if (pendingApply) { applyParsedData(pendingApply.data, pendingApply.file); setPendingApply(null); }
    },
    [pendingApply, applyParsedData],
  );

  const handleBsClose = useCallback(() => {
    setBsConfig(null);
    if (pendingApply) { applyParsedData(pendingApply.data, pendingApply.file); setPendingApply(null); }
  }, [pendingApply, applyParsedData]);

  const handleDownload = useCallback(async () => {
    setErrorMessage("");
    try {
      await downloadTemplate(getQueryParam("itineraryId"));
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to download template";
      setErrorMessage(msg);
      console.error("Failed to download template:", error);
    }
    onDownloadTemplate?.();
  }, [getQueryParam, onDownloadTemplate]);

  const handleReUpload = useCallback(() => { reUploadRef.current?.click(); }, []);

  return {
    isExcelBypass,
    isUploading,
    isServerUploading,
    uploadedFileName,
    errorMessage,
    bsConfig,
    fileRef,
    reUploadRef,
    handleFileChange,
    handleBsAction,
    handleBsClose,
    handleDownload,
    handleReUpload,
  };
}
